import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Library, Lock, BookOpen, Sparkles, PenLine, Calculator, FlaskConical, Languages, Info, Send, MessagesSquare, Globe, Heart, HandHeart, Search, SlidersHorizontal, X } from "lucide-react";
import AssignModal from "@/pages/lessons/AssignModal";

const SUBJECT_ICON = { "book-open": BookOpen, sparkles: Sparkles, "pen-line": PenLine, calculator: Calculator, "flask-conical": FlaskConical, languages: Languages, "messages-square": MessagesSquare, globe: Globe, heart: Heart, "hand-heart": HandHeart };

const GRADE_ORDER = ["Pre-K", "PreK", "Kindergarten", "K", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];
const gradeRank = (g) => { const i = GRADE_ORDER.indexOf(g || ""); return i === -1 ? 99 : i; };
const sel = "rounded-xl border border-[#E5DECF] bg-white px-3 py-2 font-body text-sm text-[#1F2937] focus:outline-none focus:ring-2 focus:ring-[#0D9488]/30";

function Toggle({ on, disabled, onClick, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${on ? "bg-[#0D9488]" : "bg-[#CBD5E1]"}`} aria-label="Toggle">
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

export default function LibraryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [assignLesson, setAssignLesson] = useState(null);
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [grade, setGrade] = useState("all");
  const [subjectF, setSubjectF] = useState("all");
  const [status, setStatus] = useState("all");   // all | on | off | free
  const [sort, setSort] = useState("default");   // default | grade | title

  const load = async () => {
    try { setData(await api.getLibrary()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const audience = data?.audience || "students";
  const isClient = data?.is_client;

  const toggleLesson = async (subject, lesson) => {
    setBusy(true);
    // optimistic
    setData((d) => ({ ...d, subjects: d.subjects.map((s) => s.id !== subject.id ? s : {
      ...s, lessons: s.lessons.map((l) => l.id !== lesson.id ? l : { ...l, enabled: !l.enabled }),
      enabled_count: s.enabled_count + (lesson.enabled ? -1 : 1),
    }) }));
    try { await api.toggleLibrary(lesson.id, !lesson.enabled); }
    catch (e) { toast.error(formatApiError(e)); load(); } finally { setBusy(false); }
  };

  const toggleSubject = async (subject, enabled) => {
    setBusy(true);
    try {
      const r = await api.toggleLibrarySubject(subject.id, enabled);
      if (r.count === 0) {
        toast.error(`No ${subject.name} lessons could be ${enabled ? "enabled" : "disabled"}${enabled ? " — locked by your provider" : ""}`);
      } else {
        toast.success(`${enabled ? "Enabled" : "Disabled"} ${r.count} ${subject.name} lesson${r.count === 1 ? "" : "s"}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (loading) return <div className="p-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;

  const grades = [...new Set(data.subjects.flatMap((s) => s.lessons.map((l) => l.grade).filter(Boolean)))].sort((a, b) => gradeRank(a) - gradeRank(b));
  const filtering = q || grade !== "all" || subjectF !== "all" || status !== "all";
  const needle = q.trim().toLowerCase();
  const visibleLessons = (s) => {
    let ls = s.lessons.filter((l) =>
      (grade === "all" || l.grade === grade) &&
      (status === "all" || (status === "on" ? l.enabled : status === "off" ? !l.enabled : l.free)) &&
      (!needle || `${l.title} ${l.objective || ""} ${l.tag || ""}`.toLowerCase().includes(needle)));
    if (sort === "grade") ls = [...ls].sort((a, b) => gradeRank(a.grade) - gradeRank(b.grade) || a.title.localeCompare(b.title));
    else if (sort === "title") ls = [...ls].sort((a, b) => a.title.localeCompare(b.title));
    return ls;
  };
  const shown = data.subjects.filter((s) => s.total > 0 && (subjectF === "all" || s.id === subjectF));
  const matchCount = shown.reduce((n, s) => n + visibleLessons(s).length, 0);
  const clear = () => { setQ(""); setGrade("all"); setSubjectF("all"); setStatus("all"); };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6" data-testid="library-page">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#7A3FF2]"><Library size={14} /> Curriculum</div>
      <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">Lesson Library</h1>
      <p className="mt-1 font-body text-[#6B7280]">
        Turn lessons on to make them available to your {audience}. {audience === "students" ? "You can then assign any enabled lesson from the Lessons page." : "Enabled packs unlock for the teachers and families under your brand."}
      </p>

      {isClient && (
        <div className="mt-4 flex items-start gap-2 rounded-2xl border border-[#7A3FF2]/30 bg-[#7A3FF2]/5 px-4 py-3 font-body text-sm text-[#652BDB]" data-testid="client-notice">
          <Info size={16} className="mt-0.5 shrink-0" /> Some lessons are controlled by your provider. Locked lessons can't be enabled until they turn them on.
        </div>
      )}

      {data.has_full_access === false && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-3xl border-2 border-[#F59E0B]/40 bg-[#FFF7E6] p-4" data-testid="lib-free-plan-banner">
          <div className="flex items-start gap-3">
            <Lock size={20} className="mt-0.5 shrink-0 text-[#B45309]" />
            <div>
              <div className="font-heading text-sm font-extrabold text-[#1F2937]">Free plan — showing free lessons only</div>
              <p className="font-body text-xs text-[#6B7280]">
                {data.premium_hidden > 0 ? `${data.premium_hidden} more lesson${data.premium_hidden === 1 ? "" : "s"} unlock with a membership.` : "Premium lessons unlock with a membership."} Your students only see the lessons you enable here.
              </p>
            </div>
          </div>
          <button onClick={() => navigate("/pricing")} data-testid="lib-upgrade-btn" className="rounded-full bg-[#F59E0B] px-4 py-2 font-heading text-sm font-bold text-white hover:bg-[#D97706]">Unlock all lessons</button>
        </div>
      )}

      <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-4" data-testid="library-filters">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#6B7280]"><SlidersHorizontal size={14} /> Filter &amp; sort</div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <label className="relative sm:col-span-2 lg:col-span-1">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lessons…" data-testid="lib-search" className={`${sel} w-full pl-9`} />
          </label>
          <select value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="lib-filter-grade" className={sel}>
            <option value="all">All grades</option>
            {grades.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={subjectF} onChange={(e) => setSubjectF(e.target.value)} data-testid="lib-filter-subject" className={sel}>
            <option value="all">All subjects</option>
            {data.subjects.filter((s) => s.total > 0).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lib-filter-status" className={sel}>
            <option value="all">All lessons</option>
            <option value="on">Enabled only</option>
            <option value="off">Not enabled</option>
            <option value="free">Free lessons</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="lib-sort" className={sel}>
            <option value="default">Sort: curriculum order</option>
            <option value="grade">Sort: grade level</option>
            <option value="title">Sort: title A–Z</option>
          </select>
        </div>
        {filtering && (
          <div className="mt-3 flex items-center justify-between gap-2 font-body text-xs text-[#6B7280]">
            <span data-testid="lib-match-count">{matchCount} lesson{matchCount === 1 ? "" : "s"} match</span>
            <button onClick={clear} data-testid="lib-clear-filters" className="inline-flex items-center gap-1 rounded-full bg-[#F2ECE1] px-3 py-1 font-bold text-[#1F2937] hover:bg-[#E5DECF]"><X size={12} /> Clear filters</button>
          </div>
        )}
      </div>

      <div className="mt-6 space-y-4">
        {filtering && matchCount === 0 && (
          <p className="rounded-2xl border border-dashed border-[#E5DECF] p-6 text-center font-body text-sm text-[#94A3B8]" data-testid="lib-no-matches">No lessons match these filters.</p>
        )}
        {shown.map((s) => {
          const Icon = SUBJECT_ICON[s.icon] || BookOpen;
          const allOn = s.enabled_count === s.total && s.total > 0;
          const unlocked = s.lessons.filter((l) => !l.reseller_locked).length;
          const isCursive = s.kind === "cursive";
          const lessons = visibleLessons(s);
          if (filtering && lessons.length === 0) return null;
          return (
            <div key={s.id} className="rounded-3xl border border-[#E5DECF] bg-white p-4 sm:p-5" data-testid={`lib-subject-${s.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl text-white" style={{ background: s.color }}><Icon size={22} /></div>
                  <div>
                    <h2 className="font-heading text-lg font-extrabold text-[#1F2937]">{s.name}</h2>
                    <p className="font-body text-xs text-[#6B7280]">
                      {isCursive
                        ? `${s.total} level${s.total === 1 ? "" : "s"} · always available`
                        : `${s.enabled_count} of ${s.total} enabled${filtering ? ` · showing ${lessons.length}` : ""}`}
                    </p>
                  </div>
                </div>
                {!isCursive && (
                  <div className="flex gap-2">
                    <button onClick={() => toggleSubject(s, true)} disabled={busy || allOn || unlocked === 0} data-testid={`lib-enable-all-${s.id}`} className="rounded-full bg-[#0D9488]/10 px-3 py-1.5 text-xs font-bold text-[#0D9488] hover:bg-[#0D9488]/20 disabled:opacity-40">Enable all</button>
                    <button onClick={() => toggleSubject(s, false)} disabled={busy || s.enabled_count === 0} data-testid={`lib-disable-all-${s.id}`} className="rounded-full bg-[#F2ECE1] px-3 py-1.5 text-xs font-bold text-[#6B7280] hover:bg-[#E5DECF] disabled:opacity-40">Disable all</button>
                  </div>
                )}
              </div>

              <div className="mt-4 divide-y divide-[#F2ECE1]">
                {lessons.map((l) => (
                  <div key={l.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3" data-testid={`lib-lesson-${l.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="break-words font-body font-semibold text-[#1F2937]" data-testid={`lib-lesson-title-${l.id}`}>{l.title}</span>
                        {l.grade && <span className="rounded-full bg-[#F2ECE1] px-2 py-0.5 text-[10px] font-bold uppercase text-[#6B7280]">{l.grade}</span>}
                        {l.official && <span className="rounded-full bg-[#7A3FF2]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-[#652BDB]" data-testid={`lib-official-${l.id}`}>Official</span>}
                        {l.free && <span className="rounded-full bg-[#10B981]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-[#047857]">Free</span>}
                        {isCursive && typeof l.activity_count === "number" && (
                          <span className="rounded-full bg-[#0D9488]/10 px-2 py-0.5 text-[10px] font-bold uppercase text-[#0B7F74]">{l.activity_count} activities</span>
                        )}
                      </div>
                      {l.objective && <p className="mt-0.5 break-words font-body text-xs text-[#94A3B8]">{l.objective}</p>}
                    </div>
                    {l.reseller_locked ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-[#94A3B8]" data-testid={`lib-locked-${l.id}`}><Lock size={13} /> Locked</span>
                    ) : (
                      <div className="flex items-center justify-end gap-3">
                        {l.enabled && audience === "students" && (
                          <button onClick={() => setAssignLesson({ id: l.id, title: l.title })} data-testid={`lib-assign-${l.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#F2ECE1] px-3 py-1.5 text-xs font-bold text-[#0D9488] hover:bg-[#E5DECF]">
                            <Send size={12} /> Assign
                          </button>
                        )}
                        {!isCursive && (
                          <Toggle on={l.enabled} disabled={busy} onClick={() => toggleLesson(s, l)} testid={`lib-toggle-${l.id}`} />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {assignLesson && <AssignModal lesson={assignLesson} onClose={() => setAssignLesson(null)} onDone={() => {}} />}
    </div>
  );
}
